import React from 'react';
import {
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { useGetOne, useRecordContext } from 'react-admin';

import useCaseStatusColors from '../../../hooks/useCaseStatusColors';
import { ICase } from '../../../utilities/schemas/case';
import {
  getTimelineStepVisualState,
  getTimelineSteps,
  timelineStepVisualStates,
} from '../utils/summaryTimeline';
import EqualSpacingStepper, { EqualSpacingStep } from '../../layout/EqualSpacingStepper';
import caseStatuses, { CaseStatuses } from '../../../constants/caseStatuses';

const CaseTimeline = () => {
  const record = useRecordContext<ICase>();
  const theme = useTheme();
  const caseStatusColors = useCaseStatusColors();

  const { data, isLoading } = useGetOne<{
    id: string;
    statusBreakdown: Record<CaseStatuses, number>;
  }>('cases', { id: `${record?.id}/status-breakdown` }, { enabled: !!record?.id });

  if (!record || isLoading) return null;

  const {
    closedAt,
    status,
  } = record;
  const closedAtLabel = closedAt ? new Date(closedAt).toLocaleString() : '-';

  const {
    currentStepIndex,
    steps,
    totalCaseDurationLabel,
  } = getTimelineSteps({
    status,
    statusBreakdown: data?.statusBreakdown,
  });

  return (
    <>
      <Box display="flex" alignItems="center" rowGap={1} columnGap={2} marginBottom={3} flexWrap="wrap">
        <Typography variant="h6">
          Status timeline
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Case duration:
          {' '}
          {totalCaseDurationLabel}
        </Typography>
      </Box>
      <EqualSpacingStepper
        steps={steps.map((step, index): EqualSpacingStep => {
          const visualState = getTimelineStepVisualState({
            currentStepIndex,
            step,
            stepIndex: index,
          });
          const isCurrent = visualState === timelineStepVisualStates.CURRENT;
          const isCompleted = visualState === timelineStepVisualStates.COMPLETED;

          const prevStep = index > 0 ? steps[index - 1] : null;
          const prevVisualState = prevStep
            ? getTimelineStepVisualState({
              currentStepIndex,
              step: prevStep,
              stepIndex: index - 1,
            })
            : null;

          const labelColor = (() => {
            if (isCurrent) return theme.palette.text.primary;
            if (isCompleted) return theme.palette.text.secondary;
            return theme.palette.text.disabled;
          })();

          return {
            key: step.status,
            label: step.label,
            subLabel: step.status === caseStatuses.CLOSED
              ? closedAtLabel
              : step.durationLabel,
            dotColor: isCurrent || isCompleted
              ? caseStatusColors[step.status]
              : theme.palette.text.disabled,
            labelColor,
            fontWeight: isCurrent ? 600 : 400,
            connectorColor: prevStep && prevVisualState === timelineStepVisualStates.COMPLETED
              ? caseStatusColors[prevStep.status]
              : theme.palette.text.disabled,
          };
        })}
      />
    </>
  );
};

export default CaseTimeline;
